import { useQuery } from "@tanstack/react-query";
import api from "../../utils/api";
import {
  REPORTING_DEFAULTS,
  classifyRisk,
  type ReportingPlatform,
  type ReportingStatus,
  type RiskBucket,
} from "./reportingConfig";

const REPORTING_STALE = 5 * 60_000;

export interface PlatformStats {
  total: number;
  reporting: number;
  not_reporting: number;
  effectiveness_pct: number;
  companies: number;
  users: number;
  altas_count?: number;
  bajas_count?: number;
  loss_pct?: number;
}

export interface NotReportingUnit {
  imei: string;
  user_id: number;
  user_name: string;
  business_name: string;
  platform: string;
  last_ts: number | null;
}

export interface ReportingSummary {
  generated_at: number;
  status: string;
  platform: string;
  threshold_sec: number;
  total: number;
  reporting: number;
  not_reporting: number;
  effectiveness_pct: number;
  companies_total: number;
  users_total: number;
  altas_count?: number;
  bajas_count?: number;
  loss_pct?: number;
  movement_at?: string;
  by_platform: Record<ReportingPlatform, PlatformStats>;
  not_reporting_units?: NotReportingUnit[];
}

export interface ReportingCompany {
  user_id: number;
  user_name: string;
  business_name: string;
  total: number;
  reporting: number;
  not_reporting: number;
  effectiveness_pct: number;
  by_platform: Partial<Record<ReportingPlatform, { total: number; reporting: number }>>;
}

export interface ReportingCompaniesResponse {
  total: number;
  returned: number;
  page: number;
  limit: number;
  pages: number;
  has_prev: boolean;
  has_next: boolean;
  critico?: boolean;
  critical_pct?: number;
  min_units?: number;
  companies: ReportingCompany[];
}

async function reportingGet<T>(
  path: string,
  params: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const cleaned: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") cleaned[k] = v;
  });
  const res = await api.get<{ data: T }>(path, { params: cleaned, signal });
  return res.data.data;
}

export interface SummaryOpts {
  platform?: ReportingPlatform;
  withUnits?: boolean;
  thresholdSec?: number;
  status?: ReportingStatus;
}

export function useReportingSummary(opts: SummaryOpts = {}) {
  const status = opts.status ?? REPORTING_DEFAULTS.status;
  const threshold = opts.thresholdSec ?? REPORTING_DEFAULTS.thresholdSec;
  return useQuery({
    queryKey: [
      "reportingSummary",
      status,
      threshold,
      opts.platform ?? "all",
      !!opts.withUnits,
    ],
    staleTime: REPORTING_STALE,
    queryFn: ({ signal }) =>
      reportingGet<ReportingSummary>(
        "/reporting/summary",
        {
          status,
          threshold,
          platform: opts.platform,
          units: opts.withUnits ? 1 : undefined,
        },
        signal,
      ),
  });
}

export interface CompaniesOpts {
  platform?: ReportingPlatform;
  critico?: boolean;
  minUnits?: number;
  thresholdSec?: number;
  status?: ReportingStatus;
}

export function useReportingCompanies(opts: CompaniesOpts = {}) {
  const status = opts.status ?? REPORTING_DEFAULTS.status;
  const threshold = opts.thresholdSec ?? REPORTING_DEFAULTS.thresholdSec;
  return useQuery({
    queryKey: [
      "reportingCompanies",
      status,
      threshold,
      opts.platform ?? "all",
      !!opts.critico,
      opts.minUnits ?? null,
    ],
    staleTime: REPORTING_STALE,
    queryFn: ({ signal }) =>
      reportingGet<ReportingCompaniesResponse>(
        "/reporting/companies",
        {
          status,
          threshold,
          platform: opts.platform,
          critico: opts.critico ? 1 : undefined,
          min_units: opts.critico
            ? (opts.minUnits ?? REPORTING_DEFAULTS.minUnits)
            : undefined,
        },
        signal,
      ).then((b) => b.companies ?? []),
  });
}

export interface ReportingUnit {
  imei: string;
  user_id: number;
  user_name: string;
  business_name: string;
  platform: string;
  last_ts: number | null;
  reporting: boolean;
  device_name?: string;
}

export interface ReportingUnitsResponse {
  total: number;
  reporting_count: number;
  not_reporting_count: number;
  effectiveness_pct: number;
  listed: number;
  page: number;
  limit: number;
  pages: number;
  has_prev: boolean;
  has_next: boolean;
  returned: number;
  units: ReportingUnit[];
}

export interface UnitsOpts {
  platform?: ReportingPlatform;
  reporting?: "yes" | "no";
  thresholdSec?: number;
  status?: ReportingStatus;
  page?: number;
  limit?: number;
}

export function useReportingUnits(opts: UnitsOpts = {}) {
  const status = opts.status ?? REPORTING_DEFAULTS.status;
  const threshold = opts.thresholdSec ?? REPORTING_DEFAULTS.thresholdSec;
  return useQuery({
    queryKey: [
      "reportingUnits",
      status,
      threshold,
      opts.platform ?? "all",
      opts.reporting ?? "all",
      opts.page ?? null,
      opts.limit ?? null,
    ],
    staleTime: REPORTING_STALE,
    queryFn: ({ signal }) =>
      reportingGet<ReportingUnitsResponse>(
        "/reporting/units",
        {
          status,
          threshold,
          platform: opts.platform,
          reporting: opts.reporting,
          page: opts.page,
          limit: opts.limit,
        },
        signal,
      ),
  });
}

export interface RiskDistribution {
  totalUnits: number;
  buckets: Record<
    RiskBucket,
    { companyCount: number; units: number; companies: ReportingCompany[] }
  >;
}

export function computeRiskDistribution(
  companies: ReportingCompany[],
  minUnits: number = REPORTING_DEFAULTS.minUnits,
): RiskDistribution {
  const buckets: RiskDistribution["buckets"] = {
    healthy: { companyCount: 0, units: 0, companies: [] },
    warning: { companyCount: 0, units: 0, companies: [] },
    highRisk: { companyCount: 0, units: 0, companies: [] },
    critical: { companyCount: 0, units: 0, companies: [] },
  };
  let totalUnits = 0;
  for (const c of companies) {
    if (c.total < minUnits) continue;
    const bucket = classifyRisk(c.effectiveness_pct);
    buckets[bucket].companyCount++;
    buckets[bucket].units += c.total;
    buckets[bucket].companies.push(c);
    totalUnits += c.total;
  }
  return { totalUnits, buckets };
}

export interface CompanyWithPriority {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  reportingUnits: number;
  totalUnits: number;
  percentage: number;
  priority: RiskBucket;
  offlineUnits: number;
}

export function toCompanyWithPriority(c: ReportingCompany): CompanyWithPriority {
  return {
    id: String(c.user_id),
    name: c.business_name || c.user_name || `#${c.user_id}`,
    email: "",
    phone: "",
    status: "",
    reportingUnits: c.reporting,
    totalUnits: c.total,
    percentage: c.effectiveness_pct,
    priority: classifyRisk(c.effectiveness_pct),
    offlineUnits: c.not_reporting,
  };
}

export interface CriticalUnitItem {
  id: string;
  name: string;
  plate: string;
  companyId: string;
  companyName: string;
}

export interface PlatformCriticalAlerts {
  companies: CompanyWithPriority[];
  units: CriticalUnitItem[];
}

export function usePlatformCriticalAlerts(
  platform: ReportingPlatform,
  opts: {
    minUnits?: number;
    thresholdSec?: number;
    status?: ReportingStatus;
  } = {},
) {
  const status = opts.status ?? REPORTING_DEFAULTS.status;
  const threshold = opts.thresholdSec ?? REPORTING_DEFAULTS.thresholdSec;
  const minUnits = opts.minUnits ?? REPORTING_DEFAULTS.minUnits;
  return useQuery({
    queryKey: ["platformCriticalAlerts", platform, status, threshold, minUnits],
    staleTime: REPORTING_STALE,
    queryFn: async ({ signal }): Promise<PlatformCriticalAlerts> => {
      const [companiesRes, summaryRes] = await Promise.all([
        reportingGet<ReportingCompaniesResponse>(
          "/reporting/companies",
          { status, threshold, platform, critico: 1, min_units: minUnits },
          signal,
        ),
        reportingGet<ReportingSummary>(
          "/reporting/summary",
          { status, threshold, platform, units: 1 },
          signal,
        ),
      ]);

      const companies = (companiesRes.companies ?? []).map(toCompanyWithPriority);
      const criticalIds = new Set(companies.map((c) => c.id));

      const rawUnits = (summaryRes.not_reporting_units ?? []).filter((u) =>
        criticalIds.has(String(u.user_id)),
      );
      const units: CriticalUnitItem[] = rawUnits.map((u) => ({
        id: u.imei,
        name: u.imei,
        plate: "",
        companyId: String(u.user_id),
        companyName: u.business_name || u.user_name || `#${u.user_id}`,
      }));

      return { companies, units };
    },
  });
}

export interface CompanyUnitsResponse {
  total: number;
  reporting_count: number;
  not_reporting_count: number;
  effectiveness_pct: number;
  units: ReportingUnit[];
}

export function useCompanyUnits(companyId: string | undefined, queryParams?: Record<string, string | number>) {
  return useQuery({
    queryKey: ["companyUnits", companyId, queryParams],
    staleTime: REPORTING_STALE,
    enabled: !!companyId,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        status: queryParams?.status?.toString() || "billable",
        threshold: queryParams?.threshold?.toString() || "86400",
        platform: queryParams?.platform?.toString() || "v5",
        ...(queryParams?.reporting && { reporting: queryParams.reporting.toString() }),
      });

      const res = await api.get<{ data: CompanyUnitsResponse }>(
        `/entregaServicio/units/${companyId}?${params.toString()}`,
        { signal }
      );
      return res.data.data;
    },
  });
}

